import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FlaskConical } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import InfoTip from '../../shared/InfoTip';
import { INVALID_SCHEMA_COPY, describeError, prettyJson } from '../mcpUtils';

/**
 * Edit one tool (§4).
 *
 * Descriptions get the most room because they are the single biggest factor in
 * whether a model calls a tool correctly. The annotations are editable too —
 * clients read `destructiveHint` to decide whether to ask the user before a
 * call, so an incorrectly-annotated write tool is a real hazard.
 */
/**
 * The API validates `input_schema` on save and answers 400 with
 * `input_schema is not usable: <reason>` — that belongs beside the editor, not
 * in the generic error box at the bottom.
 */
const SCHEMA_REFUSAL = /^input_schema is not usable/i;

const ToolEditDrawer = ({ open, server, tool, lintReason, onClose, onSaved, onTest, notify }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schemaText, setSchemaText] = useState('');
  const [readOnlyHint, setReadOnlyHint] = useState(false);
  const [destructiveHint, setDestructiveHint] = useState(false);
  const [schemaError, setSchemaError] = useState(null);
  const [schemaServerError, setSchemaServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !tool) return;
    setName(tool.tool_name || '');
    setDescription(tool.description || '');
    setSchemaText(prettyJson(tool.input_schema ?? { type: 'object', properties: {} }));
    setReadOnlyHint(tool.annotations?.readOnlyHint === true);
    setDestructiveHint(tool.annotations?.destructiveHint === true);
    setSchemaError(null);
    setSchemaServerError(null);
    setError(null);
  }, [open, tool]);

  const handleSchemaChange = (value) => {
    setSchemaText(value);
    setSchemaServerError(null);
    if (!value.trim()) {
      setSchemaError(null);
      return;
    }
    try {
      JSON.parse(value);
      setSchemaError(null);
    } catch (e) {
      setSchemaError(e.message);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('A tool needs a name.');
      return;
    }
    let inputSchema;
    try {
      inputSchema = schemaText.trim() ? JSON.parse(schemaText) : undefined;
    } catch (e) {
      setSchemaError(e.message);
      return;
    }

    setSaving(true);
    setError(null);
    setSchemaServerError(null);
    try {
      await mcpServerService.updateTool(server.id, tool.id, {
        tool_name: name.trim(),
        description: description.trim(),
        input_schema: inputSchema,
        annotations: { ...(tool.annotations || {}), readOnlyHint, destructiveHint },
      });
      notify?.('Tool saved.', 'success');
      await onSaved?.();
    } catch (e) {
      const message = describeError(e, "Couldn't save the tool.");
      if (e?.status === 400 && SCHEMA_REFUSAL.test(message)) setSchemaServerError(message);
      else setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ className: styles.drawerPaper }}
    >
      <div className={styles.drawerHeader}>
        <Typography className={styles.sectionTitle}>Edit tool</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Close">
          <Close />
        </IconButton>
      </div>

      <div className={styles.drawerBody}>
        <TextField
          label="Tool name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          helperText="This is what the model sees and calls. Lowercase with underscores works best."
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={3}
          helperText="The biggest factor in whether a model calls this correctly. Say what it does, what it needs, and what it returns."
        />

        <Box>
          <Typography className={styles.sectionTitle} sx={{ mb: 1 }}>
            Behaviour
          </Typography>
          <span className={styles.sectionHint}>
            Clients read these to decide whether to ask the user before calling.
          </span>
          <FormControlLabel
            control={
              <Switch
                checked={readOnlyHint}
                onChange={(e) => {
                  setReadOnlyHint(e.target.checked);
                  if (e.target.checked) setDestructiveHint(false);
                }}
              />
            }
            label="Read-only — never changes anything upstream"
          />
          <FormControlLabel
            control={
              <Switch
                checked={destructiveHint}
                onChange={(e) => {
                  setDestructiveHint(e.target.checked);
                  if (e.target.checked) setReadOnlyHint(false);
                }}
              />
            }
            label="Modifies data — creates, updates or deletes"
          />
        </Box>

        <Box>
          <Typography
            className={styles.sectionTitle}
            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            Input schema
            <InfoTip title={INVALID_SCHEMA_COPY.schemaTooltip} />
          </Typography>
          {lintReason && !schemaServerError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {INVALID_SCHEMA_COPY.drawerAlert(lintReason)}
            </Alert>
          )}
          <textarea
            className={styles.codeArea}
            value={schemaText}
            onChange={(e) => handleSchemaChange(e.target.value)}
            spellCheck={false}
          />
          {schemaError && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
              Not valid JSON — {schemaError}
            </Typography>
          )}
          {schemaServerError && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
              {schemaServerError}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
      </div>

      <div className={styles.drawerFooter}>
        <Button
          className={sharedStyles.subtleBtn}
          startIcon={<FlaskConical size={15} />}
          onClick={() => onTest?.(tool?.id)}
          disabled={saving}
        >
          Test
        </Button>
        <Button className={sharedStyles.subtleBtn} onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          className={sharedStyles.primaryBtn}
          variant="contained"
          onClick={handleSave}
          disabled={saving || !!schemaError}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Drawer>
  );
};

export default ToolEditDrawer;
